import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Users as UsersIcon, ShieldCheck, User } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function Users() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return { email: "", password: "", name: "", role: "user", company_id: "" };
  }

  const load = useCallback(async () => {
    setLoading(true);
    const [u, c] = await Promise.all([api.get("/users"), api.get("/companies")]);
    setItems(u.data || []);
    setCompanies(c.data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  function openCreate() {
    setEditing(null);
    setForm(empty());
    setShowForm(true);
  }
  function openEdit(u) {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, company_id: u.company_id || "" });
    setShowForm(true);
  }

  async function save() {
    if (!form.email || !form.name) return toast.error("Email and name required");
    if (!editing && !form.password) return toast.error("Password required");
    if (form.role === "user" && !form.company_id) return toast.error("Regular users need a company");
    try {
      const payload = { ...form };
      if (form.role === "admin") payload.company_id = null;
      if (editing) {
        const u = { name: payload.name, role: payload.role, company_id: payload.company_id };
        if (payload.password) u.password = payload.password;
        await api.patch(`/users/${editing.id}`, u);
      } else {
        await api.post("/users", payload);
      }
      toast.success("Saved");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete user?")) return;
    try {
      await api.delete(`/users/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="kpi-label">{t("nav.users")}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t("user.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("user.subtitle")}</p>
        </div>
        <Button onClick={openCreate} data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-1.5" /> {t("user.new")}
        </Button>
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.email")}</TableHead>
                <TableHead>{t("common.role")}</TableHead>
                <TableHead>{t("common.company")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">{t("common.loading")}</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground"><UsersIcon className="h-5 w-5 mx-auto mb-2" />{t("user.no_users")}</TableCell></TableRow>
              ) : (
                items.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-xs">{u.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={u.role === "admin" ? "text-primary border-primary/30 bg-primary/10" : ""}>
                        {u.role === "admin" ? <ShieldCheck className="h-3 w-3 mr-1" /> : <User className="h-3 w-3 mr-1" />}
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">{u.company_id ? companyName(u.company_id) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("user.edit") : t("user.new")}</DialogTitle>
            <DialogDescription>{t("user.dialog_subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="un">{t("common.name")}</Label>
              <Input id="un" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="ue">{t("common.email")}</Label>
              <Input id="ue" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-email" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="up">{t("common.password")}{editing && ` ${t("ds.password_keep")}`}</Label>
              <Input id="up" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.role")}</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">{t("user.role_admin")}</SelectItem>
                  <SelectItem value="user">{t("user.role_user")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.company")}</Label>
              <Select value={form.company_id || "_none"} onValueChange={(v) => setForm({ ...form, company_id: v === "_none" ? "" : v })}>
                <SelectTrigger data-testid="user-company"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— ({t("user.role_admin").toLowerCase()})</SelectItem>
                  {companies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="user-save">{editing ? t("common.save") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
